'use client';

// ---------------------------------------------------------------------------
// useMusicPlaylists — playlists and schedules for this property
// ---------------------------------------------------------------------------
//
// Both live behind the same two endpoints and are invalidated together, because
// a schedule names a playlist: deleting one changes what the other shows, and
// two independently cached lists would disagree on screen.
//
// Every mutation surfaces the SERVER's message. That is deliberate — the useful
// refusals here all carry detail a generic string would throw away: which
// playlists use a track, that a name is taken, that someone else edited the
// playlist while this one was open.
// ---------------------------------------------------------------------------

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/core/lib/api-client';
import { useTenantContext } from '@/core/providers/TenantProvider';

import type { PlaylistItemInput } from '../lib/track';

export interface PlaylistItem {
  id: string;
  position: number;
  trackId: string | null;
  localRef: string | null;
  localLabel: string | null;
  track: {
    id: string;
    title: string;
    artist: string | null;
    durationSec: number;
    isActive: boolean;
  } | null;
}

export interface Playlist {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  updatedAt: string;
  items: PlaylistItem[];
}

export interface Schedule {
  id: string;
  name: string;
  playlistId: string;
  daysMask: number;
  startMinute: number;
  endMinute: number;
  priority: number;
  isActive: boolean;
  playlist?: { id: string; name: string; isActive: boolean };
}

const PLAYLISTS_KEY = ['music', 'playlists'] as const;
const SCHEDULES_KEY = ['music', 'schedules'] as const;

/**
 * Every caller raises its own toast with the server's message (see the header),
 * so the global MutationCache toast is suppressed — otherwise each action
 * showed a second, bare "Success" / "Request Failed" beside it.
 */
const SELF_TOAST = { toast: 'self' } as const;

/** The message the server sent, or a fallback. */
function messageOf(err: unknown, fallback: string): string {
  const m = (err as { message?: string })?.message;
  return typeof m === 'string' && m.length > 0 ? m : fallback;
}

export function useMusicPlaylists() {
  const queryClient = useQueryClient();

  /**
   * Only hotel staff with a property can have playlists.
   *
   * This gate is not an optimisation — it is what stops the request being made
   * at all. Both endpoints go through `guardHotel` -> `withStaffAuth`, which
   * answers 403 WRONG_PORTAL_TYPE for a platform or chain user. `api-client`
   * then classifies an authenticated 403 as a FAULT and writes it to
   * error.log, so simply letting it fail would put a guaranteed error in the
   * log on every admin page load — which is exactly what happened, and what
   * the access-log split exists to prevent.
   *
   * `currentProperty` is the honest signal: absent means there is no hotel in
   * scope, so there is nothing to ask for.
   */
  const { currentProperty } = useTenantContext();
  const hasProperty = Boolean(currentProperty?.id);

  const playlistsQuery = useQuery({
    queryKey: PLAYLISTS_KEY,
    queryFn: () => api.get<{ data: Playlist[] }>('/hotel/music/playlists'),
    enabled: hasProperty,
    retry: false,
  });

  const schedulesQuery = useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: () =>
      api.get<{ data: { schedules: Schedule[]; timezone: string | null } }>(
        '/hotel/music/schedules',
      ),
    enabled: hasProperty,
    retry: false,
  });

  const invalidate = useCallback(async () => {
    // Both, always: a schedule names a playlist, so one changing can change what
    // the other displays.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PLAYLISTS_KEY }),
      queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
    ]);
  }, [queryClient]);

  const createPlaylist = useMutation({
    mutationFn: (input: { name: string; description?: string; items?: PlaylistItemInput[] }) =>
      api.post('/hotel/music/playlists', input),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const renamePlaylist = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch(`/hotel/music/playlists/${id}`, { name }),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const replaceItems = useMutation({
    mutationFn: ({
      id,
      items,
      expectedUpdatedAt,
    }: {
      id: string;
      items: PlaylistItemInput[];
      expectedUpdatedAt?: string;
    }) => api.patch(`/hotel/music/playlists/${id}`, { items, expectedUpdatedAt }),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const deletePlaylist = useMutation({
    mutationFn: (id: string) => api.delete(`/hotel/music/playlists/${id}`),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const createSchedule = useMutation({
    mutationFn: (input: {
      playlistId: string;
      name: string;
      daysMask: number;
      startMinute: number;
      endMinute: number;
      priority?: number;
    }) => api.post('/hotel/music/schedules', input),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const toggleSchedule = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/hotel/music/schedules/${id}`, { isActive }),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  const deleteSchedule = useMutation({
    mutationFn: (id: string) => api.delete(`/hotel/music/schedules/${id}`),
    meta: SELF_TOAST,
    onSuccess: invalidate,
  });

  return {
    playlists: playlistsQuery.data?.data ?? [],
    schedules: schedulesQuery.data?.data?.schedules ?? [],
    timezone: schedulesQuery.data?.data?.timezone ?? null,
    isLoading: hasProperty && playlistsQuery.isLoading,
    /**
     * True when this user has no property in scope — a platform or chain admin.
     * The UI hides the editors rather than showing them broken.
     *
     * Derived from the tenant context rather than from a failed request: the
     * request is no longer made, and inferring capability from an error meant
     * provoking one to find out.
     */
    unavailable: !hasProperty,
    createPlaylist,
    renamePlaylist,
    replaceItems,
    deletePlaylist,
    createSchedule,
    toggleSchedule,
    deleteSchedule,
    messageOf,
  };
}
